/**
 * liveReportListAdapter — maps `GET students/:id/live-reports` rows → `UILiveSessionReport`.
 *
 * API-aware (knows `tutor_id`, `tutor_display_name`, `tutor_avatar`, `statistics.scores`, …). Not imported by
 * the DS. The Rapports tab maps every row with it, and « Dernier bilan » on the home maps its one
 * row with it too (`lastReportAdapter`) — both cards read the same fields the same way.
 *
 * Two legacy rules, kept as they are:
 * - **a report without `statistics` is an absence** (`LiveReportCardComponent`: `!statistics`,
 *   the backend's canonical no-show — `LiveReport::scopeStudentNoShow`);
 * - **a V1 report has no `statistics.ongoing_student_chapters`** (`isV1LiveReport`): no chapter,
 *   and no score either — the detail page hides the V1 score, so the card does not show one.
 */
import { type UIChapter, type UILiveSessionReport, type UITutor } from "../../../ui/types"
import { toUITutor } from "../LiveSessions/liveSessionListAdapter"
import { indexSubjectsById, toUISubject, type ApiSchoolSubject } from "../shared/schoolSubjectAdapter"
import { isV1LiveReport } from "./liveReportDetailsAdapter"

/** Loose raw report shape from the student live-reports list endpoint. */
export type ApiLiveReportListItem = {
    id?: string | number
    hash?: string
    /** The tutor's own words — a flat field of `LiveReportResource`, always serialized. */
    tutor_comment?: string | null
    tutor_id?: string | number | null
    tutor_display_name?: string | null
    /** Photo of the session's tutor, or `null` — the capsule falls back to the initials. */
    tutor_avatar?: string | null
    is_rattrapage?: boolean
    statistics?: {
        live_date_time?: string
        scores?: { total_score?: number | null } | null
        ongoing_student_chapters?: Array<{
            chapter?: {
                id?: string | number
                name?: string
                school_subject_id?: string | number
            }
        }>
    } | null
    live_occurrence?: {
        date?: string
        is_replacement?: boolean
    } | null
}

/**
 * `display_name` = « Camille R. » (`User::getDisplayNameAttribute`) — all the list carries of the
 * tutor's name. It goes through the live cards' `toUITutor`, so the capsule falls back on the same
 * initials; an empty avatar path counts as none.
 */
function reportTutor(raw: ApiLiveReportListItem): UITutor {
    return toUITutor({
        id: raw.tutor_id ?? undefined,
        display_name: raw.tutor_display_name ?? undefined,
        avatar: raw.tutor_avatar?.trim() || undefined,
    })
}

function mapChapters(raw: ApiLiveReportListItem, subjectsById: Record<string, ApiSchoolSubject>): UIChapter[] {
    const ongoing = raw.statistics?.ongoing_student_chapters ?? []
    return ongoing
        .map((sc) => {
            const chapter = sc?.chapter
            if (!chapter?.id || !chapter?.name) return null
            const subject = subjectsById[String(chapter.school_subject_id)]
            return {
                id: chapter.id,
                name: chapter.name,
                subject: toUISubject(subject),
            } satisfies UIChapter
        })
        .filter((c): c is UIChapter => c != null)
}

export function toUILiveSessionReport(
    raw: ApiLiveReportListItem,
    apiSubjects: ApiSchoolSubject[] | null | undefined
): UILiveSessionReport {
    const subjectsById = indexSubjectsById(apiSubjects)
    const date = raw.statistics?.live_date_time ?? raw.live_occurrence?.date ?? ""
    const isAbsent = !raw.statistics
    const comment = raw.tutor_comment?.trim()

    return {
        id: String(raw.id ?? raw.hash ?? ""),
        hash: raw.hash ?? "",
        date,
        tutor: reportTutor(raw),
        score: isAbsent || isV1LiveReport(raw) ? null : (raw.statistics?.scores?.total_score ?? null),
        chapters: isAbsent ? [] : mapChapters(raw, subjectsById),
        comment: comment ? comment : undefined,
        isAbsent,
        isRattrapage: !!raw.is_rattrapage,
        isReplacement: !!raw.live_occurrence?.is_replacement,
    }
}

export function toUILiveSessionReports(
    rawList: ApiLiveReportListItem[] | null | undefined,
    apiSubjects: ApiSchoolSubject[] | null | undefined
): UILiveSessionReport[] {
    return (rawList ?? []).map((r) => toUILiveSessionReport(r, apiSubjects))
}
